import React, { useEffect, useRef } from 'react';
import { Theme, useTheme } from '@mui/material';

import { csTokens } from 'components/CallScribe/callScribeTheme';

export interface IAudioRingProps {
  size: number;
  inputVolume: number;
  outputVolume: number;
  active: boolean;
  children?: React.ReactNode;
  trackColor?: string;
  techColor?: string;
  customerColor?: string;
  insets?: [number, number];
}

const FRAME_INTERVAL_MS = 33;
const SMOOTHING = 0.22;
const IDLE_SPAN = 0.16;
const MAX_SPAN = 0.92;

const prefersReducedMotion = (): boolean => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

const drawArc = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  from: number,
  span: number,
  width: number,
  color: string,
  opacity: number,
): void => {
  if (span <= 0) return;
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(cx, cy, radius, from, from + span * Math.PI * 2);
  ctx.stroke();
  ctx.restore();
};

const AudioRing: React.FC<IAudioRingProps> = ({
  size,
  inputVolume,
  outputVolume,
  active,
  children,
  trackColor,
  techColor,
  customerColor,
  insets = [4, 13],
}) => {
  const theme = useTheme<Theme>();
  const tokens = csTokens(theme);
  const track = trackColor ?? tokens.glassBorder;
  const tech = techColor ?? tokens.accent;
  const customer = customerColor ?? tokens.ringCustomer;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const levelsRef = useRef({ input: inputVolume, output: outputVolume, active });
  const smoothedRef = useRef({ input: 0, output: 0 });

  levelsRef.current = { input: inputVolume, output: outputVolume, active };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;
    const techRadius = size / 2 - insets[0];
    const customerRadius = size / 2 - insets[1];
    const stroke = 3;

    const paint = (): void => {
      const { input, output, active: isActive } = levelsRef.current;
      const target = isActive ? 1 : 0;
      smoothedRef.current.input +=
        (Math.min(1, input * 2.2) * target - smoothedRef.current.input) * SMOOTHING;
      smoothedRef.current.output +=
        (Math.min(1, output * 2.2) * target - smoothedRef.current.output) * SMOOTHING;

      ctx.clearRect(0, 0, size, size);

      drawArc(ctx, cx, cy, techRadius, 0, 1, 1, track, 0.5);
      drawArc(ctx, cx, cy, customerRadius, 0, 1, 1, track, 0.32);

      const techSpan = IDLE_SPAN + smoothedRef.current.input * (MAX_SPAN - IDLE_SPAN);
      const customerSpan = IDLE_SPAN + smoothedRef.current.output * (MAX_SPAN - IDLE_SPAN);

      drawArc(
        ctx, cx, cy, techRadius, -Math.PI / 2, techSpan, stroke,
        tech, 0.45 + smoothedRef.current.input * 0.55,
      );
      drawArc(
        ctx, cx, cy, customerRadius, Math.PI / 2, customerSpan, stroke,
        customer, 0.35 + smoothedRef.current.output * 0.55,
      );
    };

    if (prefersReducedMotion()) {
      smoothedRef.current = { input: 0, output: 0 };
      paint();
      return undefined;
    }

    let raf = 0;
    let last = 0;
    const loop = (ts: number): void => {
      if (ts - last >= FRAME_INTERVAL_MS) {
        last = ts;
        paint();
      }
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);
    return () => { window.cancelAnimationFrame(raf); };
  }, [size, tech, customer, track, insets[0], insets[1]]);

  return (
    <div style={{ position: 'relative', width: size, height: size, flex: '0 0 auto' }}>
      <canvas
        ref={canvasRef}
        aria-hidden
        style={{ position: 'absolute', inset: 0, width: size, height: size }}
      />
      {children}
    </div>
  );
};

export default AudioRing;
